import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { PieChart, BookOpen } from 'lucide-react';
import AssessmentStructureViewer from './AssessmentStructureViewer';
import { 
  X, Users, Search, ChevronRight, Calendar, CheckCircle, 
  Clock, RefreshCw, 
  GraduationCap, BarChart3, TrendingUp,
  Filter
} from 'lucide-react';
import AttendanceMatrixView from './AttendanceMatrixView';
import AggregatedMarksView from './AggregatedMarksView';
import FacultyAttendanceModal from './FacultyAttendanceModal';
import LMSView from '../LMS/LMSView';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import api from '../../api';
import moment from 'moment';
import debounce from 'lodash/debounce';

// Debug logging disabled for production performance
const log = () => {};
const logTime = () => {};

// Simple dialog for student info
const SimpleStudentDialog = ({ student, onClose }) => {
  log('SimpleStudentDialog rendered');
  if (!student) return null;
  const backendUrl = api?.defaults?.baseURL || 'http://localhost:5000';
  const profileUrl = student.profilePhotoUrl || student.profilePhoto || student.photo || student.studentDetails?.profilePhotoUrl || student.studentDetails?.profilePhoto || student.studentDetails?.photo;
  const name = student.name || student.studentDetails?.name || 'Student';
  const initials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const showImage = profileUrl && typeof profileUrl === 'string' && !profileUrl.endsWith('default-profile.png');
  const imgSrc = showImage ? (profileUrl.startsWith('http') ? profileUrl : `${backendUrl}${profileUrl.startsWith('/') ? profileUrl : '/' + profileUrl}`) : null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xs flex flex-col items-center relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
        <div className="w-20 h-20 rounded-full bg-indigo-100 text-indigo-600 font-bold flex items-center justify-center mb-4 overflow-hidden border-2 border-indigo-200">
          {imgSrc ? (
            <img src={imgSrc} alt={name} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
          ) : (
            <span className="text-3xl">{initials}</span>
          )}
        </div>
        <div className="text-center">
          <div className="font-semibold text-lg text-gray-800">{name}</div>
          <div className="text-sm text-gray-500 mt-1">USN: {student.USN || student.rollNumber || 'N/A'}</div>
        </div>
      </div>
    </div>
  );
};

// Format time
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '--:--';
  return moment(timeStr, ['HH:mm', 'H:mm']).format('hh:mm A');
};

// Session Status Badge
const SessionStatusBadge = ({ status, daysRemaining, editRemainingMinutes }) => {
  const configs = {
    'marked-editable': { icon: GraduationCap, text: 'Editable', bg: 'bg-blue-50', textColor: 'text-blue-700', dot: 'bg-blue-500' },
    'marked': { icon: CheckCircle, text: 'Marked', bg: 'bg-green-50', textColor: 'text-green-700', dot: 'bg-green-500' },
    'marked-locked': { icon: X, text: 'Locked', bg: 'bg-red-50', textColor: 'text-red-700', dot: 'bg-red-500' },
    'unmarked': { icon: Clock, text: 'Pending', bg: 'bg-amber-50', textColor: 'text-amber-700', dot: 'bg-amber-500' },
    'locked': { icon: X, text: 'Locked', bg: 'bg-red-50', textColor: 'text-red-700', dot: 'bg-red-500' }
  };
  
  const config = configs[status] || configs.unmarked;
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.textColor} border`}>
      <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      <Icon className="w-3 h-3" />
      <span className="hidden sm:inline">{config.text}</span>
      {status === 'unmarked' && daysRemaining > 0 && (
        <span className="ml-0.5 text-xs">({daysRemaining}d)</span>
      )}
      {status === 'marked-editable' && editRemainingMinutes > 0 && (
        <span className="ml-0.5 text-xs">({editRemainingMinutes}m)</span>
      )}
    </div>
  );
};

// Session Details Modal
const SessionDetailsModal = ({ session, onClose, onMark, onEdit }) => {
  const [students, setStudentsRaw] = useState([]);
  const setStudents = (val) => setStudentsRaw(Array.isArray(val) ? val : []);
  const [loading, setLoading] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!session?._id) return;
    const start = Date.now();
    log('Fetching session details for:', session._id);
    setLoading(true);
    try {
      const res = await api.get(`/api/faculty/attendance/session/${session._id}`);
      if (res.data.success) {
        setStudents(res.data.data.session.attendance || []);
        logTime('Session details fetch', start);
      }
    } catch (err) {
      console.error('Error fetching details:', err);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const getStatusBadge = (status) => {
    const badges = {
      present: 'bg-emerald-100 text-emerald-700',
      absent: 'bg-rose-100 text-rose-700',
      late: 'bg-amber-100 text-amber-700',
      'on-duty': 'bg-blue-100 text-blue-700',
      medical: 'bg-purple-100 text-purple-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const isEditable = session?.sessionStatus?.canEdit === true;
  const isUnmarked = session?.sessionStatus?.status === 'unmarked';

  const presentCount = Array.isArray(students) ? students.filter(s => s.status === 'present').length : 0;
  const absentCount = Array.isArray(students) ? students.filter(s => s.status === 'absent').length : 0;

  if (!session) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col shadow-xl animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className={`px-5 py-4 rounded-t-2xl ${isEditable ? 'bg-blue-600' : isUnmarked ? 'bg-amber-600' : 'bg-emerald-600'} text-white`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="text-white/80 text-xs mb-1">{session.className}</div>
              <h3 className="text-lg font-bold">{session.subjectSnapshot?.subjectName || session.subjectId?.name}</h3>
              <p className="text-white/70 text-xs mt-1">
                {moment(session.sessionDate).format('DD MMM YYYY')} • {formatTime12Hour(session.startTime)} - {formatTime12Hour(session.endTime)}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">{presentCount}</div>
              <div className="text-xs text-emerald-600">Present</div>
            </div>
            <div className="bg-rose-50 rounded-xl p-3 text-center">
              <div className="text-2xl font-bold text-rose-600">{absentCount}</div>
              <div className="text-xs text-rose-600">Absent</div>
            </div>
          </div>

          <div className="mb-5 bg-gray-50 rounded-xl p-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Attendance</span>
              <span className="font-bold text-indigo-600">{session.statistics?.attendancePercentage || 0}%</span>
            </div>
            <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${session.statistics?.attendancePercentage || 0}%` }} />
            </div>
          </div>

          <div className="space-y-2 max-h-[40vh] overflow-y-auto">
            <h4 className="font-semibold text-gray-800 text-sm mb-2">Students ({students.length})</h4>
            <div className="flex items-center px-2 pb-1 pt-1.5 border-b border-gray-200 text-xs font-semibold text-gray-500 bg-gray-100 rounded-t-lg">
              <div className="w-8 text-right pr-2">Sl. No</div>
              <div className="flex-1 flex items-center gap-2 min-w-0">Name</div>
              <div className="w-28 text-gray-500">USN / Roll No</div>
              <div className="w-20 text-center">Status</div>
            </div>
            {loading ? (
              <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>
            ) : (
              Array.isArray(students) && students.map((s, idx) => {
                const backendUrl = api?.defaults?.baseURL || 'http://localhost:5000';
                const profileUrl = s.studentDetails?.profilePhotoUrl || s.studentDetails?.profilePhoto || s.studentDetails?.photo || s.profilePhotoUrl || s.profilePhoto || s.photo;
                const name = s.studentDetails?.name || s.name || 'Unknown';
                const usn = s.studentDetails?.USN || s.studentDetails?.rollNumber || s.USN || s.rollNumber || '-';
                const initials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
                const showImage = profileUrl && typeof profileUrl === 'string' && !profileUrl.endsWith('default-profile.png');
                const imgSrc = showImage ? (profileUrl.startsWith('http') ? profileUrl : `${backendUrl}${profileUrl.startsWith('/') ? profileUrl : '/' + profileUrl}`) : null;
                return (
                  <div key={idx} className="flex items-center px-2 py-2 bg-gray-50 rounded-lg mt-1">
                    <div className="w-8 text-right pr-2 text-xs text-gray-400 flex-shrink-0">{idx + 1}.</div>
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 font-bold flex items-center justify-center overflow-hidden border-2 border-indigo-200">
                        {imgSrc ? (
                          <img src={imgSrc} alt={name} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                        ) : (
                          <span>{initials}</span>
                        )}
                      </div>
                      <span className="font-medium text-gray-800 text-sm truncate">{name}</span>
                    </div>
                    <div className="w-28 text-xs text-gray-700 truncate flex-shrink-0">{usn}</div>
                    <div className="w-20 flex justify-center">
                      <span className={`capitalize px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(s.status)}`}> 
                        {s.status || 'Not marked'}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 rounded-b-2xl flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-100 text-sm">Close</button>
          {isUnmarked && (
            <button onClick={onMark} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm">Mark Attendance</button>
          )}
          {isEditable && (
            <button onClick={onEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">Edit</button>
          )}
        </div>
      </div>
    </div>
  );
};

// Optimized Student List Item Component
const StudentListItem = React.memo(({ student, onClick, index }) => {
  const backendUrl = api?.defaults?.baseURL || 'http://localhost:5000';
  const profileUrl = student.profilePhotoUrl || student.profilePhoto || student.photo || student.studentDetails?.profilePhotoUrl || student.studentDetails?.profilePhoto || student.studentDetails?.photo;
  const name = student.name || student.studentDetails?.name || 'Student';
  const displayInitials = name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const showImage = profileUrl && typeof profileUrl === 'string' && !profileUrl.endsWith('default-profile.png');

  const handleClick = useCallback(() => onClick(student), [student, onClick]);

  return (
    <button
      onClick={handleClick}
      className="w-full flex items-center bg-white rounded-xl shadow-sm border p-4 hover:shadow-md transition text-left mb-3"
    >
      <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 font-bold flex items-center justify-center mr-3 overflow-hidden border-2 border-indigo-200 flex-shrink-0">
        {showImage ? (
          (() => {
            const imgSrc = profileUrl.startsWith('http') ? profileUrl : `${backendUrl}${profileUrl.startsWith('/') ? profileUrl : '/' + profileUrl}`;
            return <img src={imgSrc} alt={name} className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />;
          })()
        ) : (
          <span>{displayInitials}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 truncate">{student.name}</p>
        <p className="text-xs text-slate-500">USN: {student.USN || student.rollNumber || 'N/A'}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
    </button>
  );
});

// Optimized Safe List Component
const SafeStudentList = React.memo(({ students, onStudentClick }) => {
  const parentRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(400);
  const [hasError] = useState(false);
  const resizeTimeout = useRef();

  const safeStudents = Array.isArray(students) ? students : [];
  const safeOnStudentClick = typeof onStudentClick === 'function' ? onStudentClick : () => {};

  useEffect(() => {
    function updateHeight() {
      if (parentRef.current) {
        const rect = parentRef.current.getBoundingClientRect();
        const vh = window.innerHeight;
        setContainerHeight(Math.max(200, Math.min(vh - rect.top - 40, 600)));
      }
    }
    function throttledUpdate() {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
      resizeTimeout.current = setTimeout(updateHeight, 60);
    }
    updateHeight();
    window.addEventListener('resize', throttledUpdate);
    return () => {
      window.removeEventListener('resize', throttledUpdate);
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
    };
  }, []);

  const rowHeight = 96;
  const virtualizer = useVirtualizer({
    count: safeStudents.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 3,
  });

  if (hasError || safeStudents.length === 0) {
    return (
      <div className="space-y-3 overflow-y-auto" style={{ height: containerHeight }}>
        {safeStudents.map((student, index) => (
          <StudentListItem 
            key={student._id || index} 
            student={student} 
            onClick={safeOnStudentClick} 
            index={index}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={parentRef}
      className="space-y-0 overflow-y-auto w-full"
      style={{ height: containerHeight, position: 'relative' }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map(virtualRow => {
          const student = safeStudents[virtualRow.index];
          if (!student) return null;
          return (
            <div
              key={student._id || virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${rowHeight}px`,
                transform: `translateY(${virtualRow.start}px)`
              }}
            >
              <StudentListItem
                student={student}
                onClick={safeOnStudentClick}
                index={virtualRow.index}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Main Page Component
const SubjectDetails = ({ onClose, classItem, subject, facultyId, labBatch, onMarkAttendance, attendanceRefreshToken }) => {
  const [activeView, setActiveView] = useState('students');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSessionDetails, setShowSessionDetails] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceSession, setAttendanceSession] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // ✅ Get tenant ID from classItem or subject
  const tenantId = useMemo(() => {
    return classItem?.tenantId || subject?.tenantId || localStorage.getItem('tenantId');
  }, [classItem, subject]);

  // ✅ Use the module access hook - fetches immediately when component mounts
  const { hasModuleAccess, loading: modulesLoading } = useModuleAccess(tenantId);

  // ✅ Check if lmsmanagement module is accessible
  const hasLMSAccess = useMemo(() => {
    return hasModuleAccess('lmsmanagement');
  }, [hasModuleAccess]);

  const renderCount = useRef(0);
  const openTimeRef = useRef(null);
  const studentsCacheRef = useRef({});
  const sessionsCacheRef = useRef({});
  const fetchInProgressRef = useRef(false);

  // Debug log for program details
  console.log('SubjectDetails - classItem details:', {
    programTypeId: classItem?.programTypeId,
    programId: classItem?.programId,
    classId: classItem?.classId || classItem?._id,
    className: classItem?.className,
    fullClassItem: classItem,
    tenantId: tenantId,
    hasLMSAccess: hasLMSAccess
  });

  renderCount.current++;
  log(`SubjectDetails rendered ${renderCount.current} times`);

  useEffect(() => {
    openTimeRef.current = Date.now();
    log(`Page Component MOUNTED at ${new Date().toISOString()}`);
    return () => {
      if (openTimeRef.current) {
        const openDuration = Date.now() - openTimeRef.current;
        log(`Page Component UNMOUNTED after ${openDuration}ms`);
      }
    };
  }, []);

  useEffect(() => {
    const debounced = debounce(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    debounced();
    return () => debounced.cancel();
  }, [searchTerm]);

  const classId = useMemo(() => classItem?.classId || classItem?._id, [classItem]);
  const subjectId = useMemo(() => subject?.subjectId || subject?._id, [subject]);
  
  // ============================================================
  // FIX: Properly derive batchId for lab subjects only
  // ============================================================
  const batchId = useMemo(() => {
    // Only for lab subjects
    if (!subject?.isLab) return undefined;
    
    // Check multiple sources for batch ID
    // 1. From labBatch prop (passed from ClassDetailsModal)
    if (labBatch?.batchId) return labBatch.batchId;
    if (labBatch?._id) return labBatch._id;
    if (labBatch?.id) return labBatch.id;
    
    // 2. From subject object
    if (subject?.labBatchId) return subject.labBatchId;
    if (subject?.batchId) return subject.batchId;
    if (subject?.labBatch?._id) return subject.labBatch._id;
    
    // 3. From classItem
    if (classItem?.labBatchId) return classItem.labBatchId;
    if (classItem?.batchId) return classItem.batchId;
    
    // 4. From navigation state (if passed separately)
    if (classItem?.batchId) return classItem.batchId;
    
    console.warn('[SubjectDetails] No batchId found for lab subject:', {
      subject: subject?.name,
      labBatch: labBatch,
      classItem: classItem
    });
    
    return undefined;
  }, [subject, labBatch, classItem]);

  const openAttendanceEditor = useCallback((session) => {
    if (!session) return;

    if (typeof onMarkAttendance === 'function') {
      onMarkAttendance(session);
      return;
    }

    setShowSessionDetails(false);
    setSelectedSession(null);
    setAttendanceSession(session);
    setShowAttendanceModal(true);
  }, [onMarkAttendance]);

  const getSessionAttendanceState = useCallback((session) => {
    const rawStatus = String(session?.sessionStatus?.status || '').toLowerCase();
    const isMarked = (
      session?.attendanceMarkingStatus === 'completed' ||
      session?.hasAttendanceMarked === true ||
      rawStatus === 'marked' ||
      rawStatus === 'marked-editable' ||
      rawStatus === 'marked-locked'
    );
    const isBlocked = rawStatus === 'locked' || session?.isLocked === true || session?.isBlocked === true;
    const isEditable = rawStatus === 'marked-editable' || (
      session?.attendanceMarkingStatus === 'completed' &&
      (session?.sessionStatus?.canEdit === true || session?.sessionStatus?.canMark === true || session?.canMarkAttendance === true)
    );
    const isUnmarked = !isMarked && !isBlocked && (
      session?.sessionStatus?.canMark === true ||
      session?.canMarkAttendance === true ||
      session?.sessionStatus?.canEdit === true ||
      session?.canMark === true ||
      session?.isUnblockedByAdmin === true
    );

    let displayStatus = 'unmarked';
    if (rawStatus === 'marked-editable') {
      displayStatus = 'marked-editable';
    } else if (rawStatus === 'marked-locked' || (session?.attendanceMarkingStatus === 'completed' && session?.isLocked)) {
      displayStatus = 'marked-locked';
    } else if (isMarked) {
      displayStatus = 'marked';
    } else if (isBlocked) {
      displayStatus = 'locked';
    }

    return { rawStatus, isMarked, isBlocked, isEditable, isUnmarked, displayStatus };
  }, []);

  // ============================================================
  // FIX: fetchStudents - only pass batchId for lab subjects
  // ============================================================
  const fetchStudents = useCallback(async () => {
    if (!classId || !subjectId) return;
    if (fetchInProgressRef.current) return;
    
    // Only use batchId for lab subjects
    const effectiveBatchId = subject?.isLab ? batchId : undefined;
    const cacheKey = `${classId}_${subjectId}_${effectiveBatchId || ''}`;
    
    if (studentsCacheRef.current[cacheKey]) {
      setStudents(studentsCacheRef.current[cacheKey]);
      return;
    }
    
    fetchInProgressRef.current = true;
    setLoading(true);
    
    try {
      // Build URL with facultyId as query parameter
      let url = `/api/faculty/classes/${classId}/subjects/${subjectId}/students?facultyId=${facultyId}`;
      
      // Only add batchId for lab subjects
      if (subject?.isLab && effectiveBatchId) {
        url += `&batchId=${effectiveBatchId}`;
        console.log(`[fetchStudents] 🎯 LAB SUBJECT - filtering by batch: ${effectiveBatchId}`);
      } else if (subject?.isLab && !effectiveBatchId) {
        console.warn(`[fetchStudents] ⚠️ LAB SUBJECT but no batchId available!`);
      } else {
        console.log(`[fetchStudents] 📚 THEORY SUBJECT - no batch filter`);
      }
      
      console.log(`[fetchStudents] Fetching students with URL: ${url}`);
      console.log(`[fetchStudents] Faculty ID: ${facultyId}`);
      console.log(`[fetchStudents] Is Lab: ${subject?.isLab}, BatchId: ${effectiveBatchId}`);
      
      const res = await api.get(url);
      
      if (res.data?.success) {
        const studentsList = res.data.data.students?.list || [];
        console.log(`[fetchStudents] Found ${studentsList.length} students`);
        setStudents(studentsList);
        studentsCacheRef.current[cacheKey] = studentsList;
      } else {
        console.warn('[fetchStudents] API returned success:false');
        setStudents([]);
        studentsCacheRef.current[cacheKey] = [];
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudents([]);
      studentsCacheRef.current[cacheKey] = [];
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [classId, subjectId, batchId, facultyId, subject?.isLab]);

  // ============================================================
  // FIX: fetchSessions - only pass batchId for lab subjects
  // ============================================================
  const fetchSessions = useCallback(async () => {
    if (!classId || !subjectId || !facultyId) return;
    if (fetchInProgressRef.current) return;
    
    // Only use batchId for lab subjects
    const effectiveBatchId = subject?.isLab ? batchId : undefined;
    const cacheKey = `${classId}_${subjectId}_${effectiveBatchId || ''}_${statusFilter}`;
    
    if (sessionsCacheRef.current[cacheKey]) {
      setSessions(sessionsCacheRef.current[cacheKey]);
      return;
    }
    
    fetchInProgressRef.current = true;
    setSessionsLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('classId', classId);
      params.append('subjectId', subjectId);
      
      // Only add labBatchId for lab subjects
      if (subject?.isLab && effectiveBatchId) {
        params.append('labBatchId', effectiveBatchId);
        console.log(`[fetchSessions] 🎯 LAB SUBJECT - filtering sessions by batch: ${effectiveBatchId}`);
      }
      
      if (statusFilter !== 'all') params.append('statusFilter', statusFilter);
      
      const url = `/api/faculty/${facultyId}/attendance/all-with-status?${params}`;
      const res = await api.get(url);
      
      if (res.data.success) {
        let filtered = res.data.data.sessions || [];
        filtered = filtered.filter(s => {
          const sessionClassId = s.classId?._id || s.classId;
          const sessionSubjectId = s.subjectId?._id || s.subjectId;
          return sessionClassId?.toString() === classId?.toString() &&
                 sessionSubjectId?.toString() === subjectId?.toString();
        });
        
        setSessions(filtered);
        sessionsCacheRef.current[cacheKey] = filtered;
      } else {
        setSessions([]);
        sessionsCacheRef.current[cacheKey] = [];
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setSessions([]);
      sessionsCacheRef.current[cacheKey] = [];
    } finally {
      setSessionsLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [classId, subjectId, batchId, facultyId, statusFilter, subject?.isLab]);

  useEffect(() => {
    studentsCacheRef.current = {};
    sessionsCacheRef.current = {};
  }, [classId, subjectId, batchId]);

  useEffect(() => {
    sessionsCacheRef.current = {};
    if (activeView === 'history' || activeView === 'matrix') {
      fetchSessions();
    }
  }, [attendanceRefreshToken, activeView, fetchSessions]);

  useEffect(() => {
    if (activeView === 'students') {
      fetchStudents();
    } else if (activeView === 'history') {
      fetchSessions();
    }
  }, [activeView, fetchStudents, fetchSessions]);

  const filteredStudents = useMemo(() => {
    const studentsArray = Array.isArray(students) ? students : [];
    if (!debouncedSearchTerm) return studentsArray;
    const lowerSearch = debouncedSearchTerm.toLowerCase();
    return studentsArray.filter(s => {
      if (!s) return false;
      return s.name?.toLowerCase().includes(lowerSearch) || s.USN?.toLowerCase().includes(lowerSearch);
    });
  }, [students, debouncedSearchTerm]);

  const paginatedSessions = useMemo(() => {
    const sessionsArray = Array.isArray(sessions) ? sessions : [];
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sessionsArray.slice(start, end);
  }, [sessions, currentPage, itemsPerPage]);
  
  const totalPages = Math.ceil((Array.isArray(sessions) ? sessions.length : 0) / itemsPerPage);

  const handleSessionClick = useCallback(async (session) => {
    setSelectedSession(session);
    setShowSessionDetails(true);
  }, []);

  const handleViewChange = useCallback((view) => {
    setActiveView(view);
    setCurrentPage(1);
    if (view !== 'students' && view !== 'lms') {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, []);

  const handleStatusFilterChange = useCallback((filter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  }, []);

  const handleStudentClick = useCallback((student) => {
    setSelectedStudent(student);
  }, []);

  const memoizedStudentClick = useMemo(() => handleStudentClick, [handleStudentClick]);

  const isLab = subject?.isLab || false;
  const isElective = !!subject?.electiveGroupId;

  const finalProgramTypeId = classItem?.programTypeId || subject?.programTypeId;
  const finalProgramId = classItem?.programId || subject?.programId;

  console.log('Final program details for AssessmentStructureViewer:', {
    programTypeId: finalProgramTypeId,
    programId: finalProgramId
  });

  // ✅ Show loading state while modules are being fetched
  if (modulesLoading) {
    return (
      <div className="min-h-screen w-full flex flex-col bg-white">
        <div className="px-5 py-4 bg-indigo-600 text-white flex-shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="text-white/80 text-xs mb-1">{classItem?.className}</div>
              <h2 className="text-xl font-bold truncate">{subject?.name}</h2>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-500 mb-2" />
            <p className="text-gray-500 text-sm">Loading module access...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen w-full flex flex-col bg-white">
        {/* Header */}
        <div className={`px-5 py-4 ${isLab ? 'bg-purple-600' : isElective ? 'bg-emerald-600' : 'bg-indigo-600'} text-white flex-shrink-0`}>
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-1 text-white/80 text-xs mb-1">
                <span className="truncate">{classItem?.className}</span>
                <span>•</span>
                <span className="truncate">{subject?.code || subject?.subjectCode}</span>
                {isLab && labBatch && (
                  <span className="ml-2 bg-white/20 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">
                    Batch {labBatch.batchNumber || labBatch.number || labBatch.name || 'Unknown'}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold truncate">{subject?.name}</h2>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Tabs */}
          <div className="flex gap-5 text-sm font-medium pt-3 overflow-x-auto">
            <button 
              onClick={() => handleViewChange('students')}
              className={`pb-2 border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${activeView === 'students' ? 'border-white text-white' : 'border-transparent text-white/60'}`}
            >
              <Users className="w-4 h-4" />
              <span>Students</span>
            </button>
            <button 
              onClick={() => handleViewChange('history')}
              className={`pb-2 border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${activeView === 'history' ? 'border-white text-white' : 'border-transparent text-white/60'}`}
            >
              <Calendar className="w-4 h-4" />
              <span>Attendance Sessions</span>
            </button>
            <button 
              onClick={() => handleViewChange('matrix')}
              className={`pb-2 border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${activeView === 'matrix' ? 'border-white text-white' : 'border-transparent text-white/60'}`}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Attendance</span>
            </button>
            <button 
              onClick={() => handleViewChange('aggregatedMarks')}
              className={`pb-2 border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${activeView === 'aggregatedMarks' ? 'border-white text-white' : 'border-transparent text-white/60'}`}
            >
              <TrendingUp className="w-4 h-4" />
              <span>View Overall Marks</span>
            </button>
            <button 
              onClick={() => handleViewChange('assessment')}
              className={`pb-2 border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${activeView === 'assessment' ? 'border-white text-white' : 'border-transparent text-white/60'}`}
            >
              <PieChart className="w-4 h-4" />
              <span>Marks Entry</span>
            </button>
            {/* ✅ LMS TAB - Only show if lmsmanagement module is accessible */}
            {hasLMSAccess && (
              <button 
                onClick={() => handleViewChange('lms')}
                className={`pb-2 border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${activeView === 'lms' ? 'border-white text-white' : 'border-transparent text-white/60'}`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Learning Resources</span>
              </button>
            )}
          </div>
        </div>

        {/* Content Body Views */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* STUDENTS VIEW */}
          {activeView === 'students' && (
            <>
              <div className="px-5 py-3 bg-slate-50 border-b flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search by name or USN..."
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Total: {filteredStudents.length} students
                  {searchTerm && ` (filtered from ${students.length})`}
                  {isLab && labBatch && (
                    <span className="ml-3 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-[10px] font-semibold">
                      Batch {labBatch.batchNumber || labBatch.number || 'Unknown'}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 p-5 bg-slate-50/30 overflow-hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <RefreshCw className="w-8 h-8 animate-spin text-indigo-500 mb-2" />
                    <p>Loading students...</p>
                  </div>
                ) : filteredStudents.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">
                      {searchTerm ? 'No students match your search' : 'No students found'}
                      {isLab && batchId && (
                        <span className="block text-xs text-slate-400 mt-1">
                          For Batch {labBatch?.batchNumber || 'Unknown'}
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <SafeStudentList 
                    students={filteredStudents}
                    onStudentClick={memoizedStudentClick}
                  />
                )}
              </div>
            </>
          )}

          {/* HISTORY VIEW */}
          {activeView === 'history' && (
            <>
              <div className="px-5 py-3 bg-slate-50 border-b flex-shrink-0">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'marked', label: 'Marked' },
                    { value: 'editable', label: 'Editable' },
                    { value: 'unmarked', label: 'Pending' },
                    { value: 'locked', label: 'Locked' }
                  ].map(opt => (
                    <button 
                      key={opt.value} 
                      onClick={() => handleStatusFilterChange(opt.value)} 
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusFilter === opt.value ? 'bg-indigo-600 text-white' : 'bg-white border text-slate-600'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className="mt-2 px-3 py-1.5 border rounded-lg text-xs flex items-center gap-1 bg-white">
                  <Filter className="w-3.5 h-3.5" /> Filters {showFilters ? '▲' : '▼'}
                </button>
                {showFilters && (
                  <div className="mt-2 flex gap-2">
                    <input type="date" onChange={() => {}} className="flex-1 px-2 py-1 text-xs border rounded" placeholder="Start" />
                    <input type="date" onChange={() => {}} className="flex-1 px-2 py-1 text-xs border rounded" placeholder="End" />
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-5 bg-slate-50/30">
                {sessionsLoading ? (
                  <div className="text-center py-12">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
                    <p className="mt-2 text-sm">Loading sessions...</p>
                  </div>
                ) : paginatedSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No sessions found</p>
                    {isLab && batchId && (
                      <p className="text-xs text-slate-400 mt-1">
                        For Batch {labBatch?.batchNumber || 'Unknown'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paginatedSessions.map(session => {
                      const { displayStatus, isUnmarked, isEditable } = getSessionAttendanceState(session);

                      return (
                        <div key={session._id} onClick={() => handleSessionClick(session)} className={`border rounded-xl p-4 cursor-pointer hover:shadow-md transition ${isUnmarked ? 'bg-amber-50 border-amber-200' : isEditable ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">{moment(session.sessionDate).format('DD MMM YYYY')}</p>
                              <p className="text-xs text-gray-500">{formatTime12Hour(session.startTime)} - {formatTime12Hour(session.endTime)}</p>
                            </div>
                            <SessionStatusBadge status={displayStatus} editRemainingMinutes={session.sessionStatus?.editRemainingMinutes} />
                          </div>
                          <div className="flex justify-between items-center mt-2 pt-2 border-t">
                            <div className="flex gap-3 text-sm">
                              <span className="text-emerald-600">P: {session.statistics?.presentCount || 0}</span>
                              <span className="text-rose-600">A: {session.statistics?.absentCount || 0}</span>
                            </div>
                            <span className="font-bold text-indigo-600">{session.statistics?.attendancePercentage || 0}%</span>
                          </div>
                          <div className="mt-2 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                            <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${session.statistics?.attendancePercentage || 0}%` }} />
                          </div>
                          <div className="flex gap-2 mt-3 pt-2 border-t">
                            <button onClick={(e) => { e.stopPropagation(); handleSessionClick(session); }} className="flex-1 py-1.5 border rounded-lg text-xs font-medium">View</button>
                            {isUnmarked && (
                              <button onClick={(e) => { e.stopPropagation(); openAttendanceEditor(session); }} className="flex-1 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium">Mark</button>
                            )}
                            {isEditable && (
                              <button onClick={(e) => { e.stopPropagation(); openAttendanceEditor(session); }} className="flex-1 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium">Edit</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t">
                    <button 
                      disabled={currentPage === 1} 
                      onClick={() => setCurrentPage(p => p - 1)} 
                      className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                    <button 
                      disabled={currentPage === totalPages} 
                      onClick={() => setCurrentPage(p => p + 1)} 
                      className="px-3 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* MATRIX VIEW */}
          {activeView === 'matrix' && (
            <div className="flex-1 overflow-y-auto p-5 bg-slate-50/30">
              <AttendanceMatrixView
                facultyId={facultyId}
                classItem={classItem}
                subject={subject}
                labBatch={labBatch}
                isActive={activeView === 'matrix'}
                onRefresh={fetchSessions}
                onMarkAttendance={onMarkAttendance}
                attendanceRefreshToken={attendanceRefreshToken}
              />
            </div>
          )}

{activeView === 'aggregatedMarks' && (
  <div className="flex-1 overflow-y-auto p-5 bg-slate-50/30">
    <AggregatedMarksView
      classItem={classItem}
      subject={subject}
      facultyId={facultyId}
      tenantId={classItem?.tenantId || subject?.tenantId}
      isActive={activeView === 'aggregatedMarks'}
      programTypeId={finalProgramTypeId}
      programId={finalProgramId}
      academicYearId={classItem?.academicYear?._id}
      termId={classItem?.termInfo?._id}
      semesterId={classItem?.semesterInfo?._id}
      batchId={batchId}           // ✅ ADD THIS
      batchNumber={labBatch?.batchNumber}  // ✅ ADD THIS
    />
  </div>
)}

{activeView === 'assessment' && (
  <div className="flex-1 overflow-y-auto p-5 bg-slate-50/30">
    <AssessmentStructureViewer
      subjectId={subject?._id || subject?.subjectId}
      subjectName={subject?.name}
      facultyId={facultyId}
      isActive={activeView === 'assessment'}
      classItem={{
        ...classItem,
        classId: classItem?.classId || classItem?._id
      }}
      subject={subject}
      programTypeId={finalProgramTypeId}
      programId={finalProgramId}
      batchId={batchId}           // ✅ ADD THIS
      batchNumber={labBatch?.batchNumber}  // ✅ ADD THIS
      labBatch={labBatch}         // ✅ ADD THIS
    />
  </div>
)}

          {/* ✅ LMS VIEW - Only shown if lmsmanagement is accessible */}
          {activeView === 'lms' && hasLMSAccess && (
            <div className="flex-1 overflow-y-auto p-5 bg-slate-50/30">
              <LMSView
                classItem={classItem}
                subject={subject}
                labBatch={labBatch}
                facultyId={facultyId}
                isActive={activeView === 'lms'}
              />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedStudent && (
        <SimpleStudentDialog student={selectedStudent} onClose={() => setSelectedStudent(null)} />
      )}
      {showSessionDetails && (
        <SessionDetailsModal 
          session={selectedSession} 
          onClose={() => { setShowSessionDetails(false); setSelectedSession(null); }}
          onMark={() => {
            setShowSessionDetails(false);
            openAttendanceEditor(selectedSession);
          }}
          onEdit={() => {}}
        />
      )}
      {!onMarkAttendance && showAttendanceModal && attendanceSession && (
        <FacultyAttendanceModal
          isOpen={showAttendanceModal}
          onClose={() => {
            setShowAttendanceModal(false);
            setAttendanceSession(null);
          }}
          onSuccess={() => {
            setShowAttendanceModal(false);
            setAttendanceSession(null);
            fetchSessions();
          }}
          session={attendanceSession}
          facultyId={facultyId}
        />
      )}
    </>
  );
};

export default SubjectDetails;